import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Sparkles, ArrowDown } from "lucide-react";
import Snowfall from "./Snowfall";
import { MaskedLine, GoldButton, GhostButton, EASE } from "./shared";
import { BOOKING_URL, IMAGES } from "./content";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} id="top" className="relative flex min-h-screen items-center overflow-hidden">
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(1100px 620px at 72% 28%, rgba(212,175,55,0.14), transparent 60%), radial-gradient(900px 600px at 12% 85%, rgba(139,30,30,0.22), transparent 60%), #06120E",
                }}
            />
            <Snowfall count={70} />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-20 lg:pt-24">
                <motion.div style={{ y: textY }} className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.32em] text-gold sm:text-xs"
                        data-testid="hero-eyebrow"
                    >
                        <span className="h-px w-12 bg-gold/60" />
                        Christmas Mini Sessions · Wickford, Essex
                    </motion.p>

                    <h1 className="mt-7 font-serif text-[2.6rem] font-bold leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-7xl xl:text-[5.1rem]">
                        <MaskedLine delay={0.25}>This Christmas,</MaskedLine>
                        <MaskedLine delay={0.4}>
                            frame the{" "}
                            <em className="text-goldgrad font-serif italic">magic.</em>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
                    >
                        Twenty minutes inside two storybook sets: a vintage red sleigh in the snow
                        and a cosy Lapland workshop, photographed with the people you love, pets
                        included.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <GoldButton href={BOOKING_URL} testId="hero-cta-button" className="sm:text-base">
                            <Sparkles className="h-4 w-4" />
                            Book Earlybird for £49
                        </GoldButton>
                        <GhostButton href="#included" testId="hero-secondary-cta">
                            What's included
                        </GhostButton>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.1 }}
                        className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-mist"
                    >
                        <span className="flex items-center gap-1.5" data-testid="hero-rating-badge">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                            ))}
                            <span className="ml-1 font-semibold text-cream">5.0</span> from 23 Google reviews
                        </span>
                        <span className="hidden h-1 w-1 rounded-full bg-gold/60 sm:block" />
                        <span>Established 2015</span>
                    </motion.div>
                </motion.div>

                <motion.div style={{ y: imgY }} className="relative lg:col-span-5" data-testid="hero-visual">
                    <motion.div
                        initial={{ opacity: 0, y: 60, rotate: 2 }}
                        animate={{ opacity: 1, y: 0, rotate: 0 }}
                        transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
                        className="relative mx-auto w-72 sm:w-80 lg:ml-auto lg:w-full lg:max-w-md"
                    >
                        <div className="overflow-hidden rounded-b-3xl rounded-t-[999px] border border-gold/35 shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
                            <img
                                src={IMAGES.sleigh}
                                alt="Two children smiling on Santa's red sleigh set surrounded by frosted Christmas trees at Essex Photography Studio"
                                className="aspect-[3/4] w-full object-cover"
                                style={{ objectPosition: "center 76%" }}
                                loading="eager"
                            />
                        </div>
                        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
                            Santa's Sleigh · Set 01
                        </p>

                        <motion.div
                            initial={{ opacity: 0, y: 40, rotate: -9 }}
                            animate={{ opacity: 1, y: 0, rotate: -6 }}
                            transition={{ duration: 1.1, delay: 0.85, ease: EASE }}
                            className="absolute -bottom-14 -left-10 hidden w-40 sm:block lg:-left-20 lg:w-44"
                            data-testid="hero-polaroid"
                        >
                            <div className="bg-cream p-2 pb-6 shadow-[0_18px_40px_rgba(0,0,0,0.5)]">
                                <img
                                    src={IMAGES.toyshop}
                                    alt="Two children beside wrapped presents on the Lapland Toy Shop Christmas set"
                                    className="aspect-square w-full object-cover"
                                />
                                <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-pine-900">
                                    Lapland Workshop
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -right-4 -top-8 flex h-24 w-24 items-center justify-center rounded-full bg-crimson text-center shadow-[0_14px_36px_rgba(196,30,58,0.45)] sm:-right-6 lg:h-28 lg:w-28"
                            data-testid="hero-price-badge"
                        >
                            <div>
                                <span className="block font-serif text-2xl font-bold text-cream lg:text-3xl">£49</span>
                                <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-gold-light">
                                    earlybird
                                </span>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.a
                href="#sets"
                style={{ opacity: fade }}
                className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-gold/80 transition-colors hover:text-gold"
                aria-label="Scroll to the sets"
                data-testid="hero-scroll-cue"
            >
                <ArrowDown className="h-5 w-5 animate-bounce" />
            </motion.a>
        </section>
    );
}
